import { FC, useEffect, useRef, useState } from 'react'
import { useNavigate, useRevalidator } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

// COMPONENTS
import * as Forms from '@components/forms'
import { Column } from '@components/templates/column'
import Button from '@components/atoms/button'
import Spinner from '@components/atoms/spinner'

// TYPES
import type { PaymentMethod, SetupIntent, StripeError } from '@stripe/stripe-js'
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { usePledgeOutletContext } from '../../..'


const formId = "add-pm-form"
const Component: FC<{}> = () => {
    const { revalidate } = useRevalidator()
    const [{ }, setState] = usePledgeOutletContext()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [goBackCount, setgoBackCount] = useState(0)

    const [isLoading, setIsLoading] = useState(false)
    const [validation, setValidation] = useState<'card' | 'billing'>('card')
    const [validCard, setValidCard] = useState<boolean>()
    const [validBilling, setValidBilling] = useState<boolean>()

    useEffect(() => {
        if (validation === 'billing') {
            const title = document.getElementById('add-card-title')
            if (!!title) title.innerText = 'Billing Address'
            return () => {
                const title = document.getElementById('add-card-title')
                if (!!title) title.innerText = 'Add New Payment Method'
            }
        }
    }, [validation === 'billing'])

    const ref = useRef<HTMLDialogElement>(null)
    useEffect(() => {
        if (ref.current) {
            if (isLoading) ref.current.showModal()
            else ref.current.close()
        }
    }, [isLoading])

    function handleCardCreated(setupIntent: SetupIntent, paymentMethod: PaymentMethod) {
        console.log("Add Payment Method:", { setupIntent, paymentMethod })
        setState((currentState) => ({
            ...currentState,
            setupIntent,
            pledge: {
                ...currentState.pledge,
                paymentMethod: {
                    type: 'credit_card',
                    token: paymentMethod.id,
                    expirationMonth: `${paymentMethod.card?.exp_month || ''}`,
                    expirationYear: `${paymentMethod.card?.exp_year || ''}`.slice(-2),
                    cardType: paymentMethod.card?.brand,
                    lastFour: paymentMethod.card?.last4,
                    name: paymentMethod.metadata?.nickname || '',
                },
            },
        }))
        setTimeout(() => {
            setIsLoading(false)
            queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
            revalidate()
            goBack()
        }, 1000)
    }

    function handleCardError(error: StripeError) {
        console.log("Stripe Error:", { error })
        setIsLoading(false)
    }

    function handleClick() {
        setValidation('card')
        setgoBackCount(prev => prev + 1)
    }

    function goBack() {
        navigate('../')
    }

    return (<>
        <Forms.AddNewCard id={formId} method="POST" goBackCount={goBackCount}
            onValidCard={(isValid) => { setValidCard(isValid) }}
            onValidBillingAddress={(isValid) => { setValidBilling(isValid) }}
            onSubmit={() => {
                if (validation === 'billing') setIsLoading(true)
                else setValidation('billing')
            }}
            onCardCreated={handleCardCreated}
            onCardError={handleCardError}
        />
        <Column.Footer>
            {validation === 'card' &&
                <>
                    <Button theme='secondary' onClick={goBack}>Back</Button>
                    <Button form={formId} type="submit" disabled={!validCard}>Next</Button>
                </>}
            {validation === 'billing' &&
                <>
                    <Button theme='secondary' onClick={handleClick} disabled={isLoading}>Back</Button>
                    <Button form={formId} type="submit" disabled={(!validCard || !validBilling) || isLoading}>Add Card</Button>
                </>}
        </Column.Footer>
        <dialog ref={ref}>
            <div>
                <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <Spinner />
                </div>
            </div>
        </dialog>
    </>)
}

const AddCardRoute: RouteObject = {
    path: "add",
    Component,
}

export default AddCardRoute
